
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import ComboBox from "../../../ComboBoxes/ComboBox";
import { useDbContext } from "../../../../context/DbContext";
import { useSelector, useDispatch } from "react-redux";
import { selectMenuPermissions } from "../../../../app/permissionsSlice";
import { selectGeneralSettings, setSoftwareSettings } from "../../../../app/settingsSlice";
import axios from "axios";
import {
  FiSearch,
  FiDroplet,
  FiSliders,
  FiDollarSign,
  FiCheck,
  FiChevronDown,
  FiSave,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

// Descriptions map for settings rules
const SETTING_DESCRIPTIONS = {
  "AGENT WISE ACCOUNT NUMBER IN SCHEME RECEIPT":
    "Generate automated agent-specific account code numbering prefix on installment vouchers.",
  "READ ONLY RATE FIELD FOR EMPLOYEES IN SCHEME RECEIPT":
    "Locks the gold booking rate input box to prevent unauthorized cashier rate alterations.",
  "READ ONLY DATE FIELD FOR EMPLOYEES IN SCHEME RECEIPT":
    "Restricts backdated entries by binding receipt creation strictly to server clock.",
  "READ ONLY AGENT FIELD FOR EMPLOYEES IN SCHEME REPORT":
    "Locks the agent selection field for employee users in the Scheme Report filter panel.",
  "READ ONLY ACCOUNT NO FIELD FOR CUSTOMERS IN SCHEME REPORT":
    "Locks the account number field for customer users in the Scheme Report filter panel.",
  "ENABLE WHATSAPP ON SCHEME RECEIPT":
    "Trigger instantaneous PDF receipt dispatch directly to customer's WhatsApp mobile.",
  "ENABLE OB IN SCHEME RECEIPT PRINT":
    "Include previous Opening Balance (OB) accumulation and running weight totals on printed slips.",
  "LOCATION WISE PROFIT AND LOSS ACCOUNT":
    "Separate balance sheets and P&L financial auditing ledger independently by showroom location.",
  "DATE WISE RATE SELECTION":
    "Look up historical bullion rates table automatically when backfilling pending accounting receipts.",
  "SHOW LIVE RATES IN HOME PAGE":
    "Stream active Gold 916, Silver and Old Gold market quotes in the right HUD card widget.",
};

const GeneralSettings = forwardRef(function GeneralSettings({
  openWhatsAppSettings,
  openSmsSettings,
  onClose,
}, ref) {
  const dispatch = useDispatch();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const [userType, setuserType] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const storeduType = sessionStorage.getItem("userType");
    setuserType(storeduType);
  }, []);

  const permissions = useSelector(selectMenuPermissions);
  const GeneralSettingsRedux = useSelector(selectGeneralSettings);
  const [stringTypeSettings, setstringTypeSettings] = useState([]);
  const [filteredSettings, setFilteredSettings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCheckedOnly, setShowCheckedOnly] = useState(false);
  const [formValues, setFormValues] = useState({
    decimalPoint: "",
    inputColor: "#FFD700",
    cashAccount: "",
    toolbarSales: "",
    stockMethod: "",
    defaultLocation: "",
    decimalPointInReports: "",
  });
  const [updatedSettings, setUpdatedSettings] = useState([]);

  // Extract settings values and populate form state
  useEffect(() => {
    if (GeneralSettingsRedux && GeneralSettingsRedux.length > 0) {
      const stringSettings = GeneralSettingsRedux.filter(
        (item) => item.Type === "S"
      );

      setstringTypeSettings(stringSettings);
      setFilteredSettings(stringSettings);
      setUpdatedSettings(GeneralSettingsRedux);

      const settingsMap = {};
      GeneralSettingsRedux.forEach((setting) => {
        settingsMap[setting.Name] = setting.s_Value;
      });

      setFormValues({
        decimalPoint: settingsMap["DECIMAL POINT"] || "3",
        inputColor: settingsMap["INPUT COLOUR"] || "#FFD700",
        cashAccount: settingsMap["CASH A/C"] || "CASH",
        toolbarSales: settingsMap["TOOLBAR SALES"] || "2",
        stockMethod: settingsMap["STOCK METHOD"] || "REAL",
        defaultLocation: settingsMap["LOCATION"] || "SHOP",
        decimalPointInReports: settingsMap["DECIMAL POINT IN REPORT"] || "2",
      });
    }
  }, [GeneralSettingsRedux]);

  // Apply filters whenever search term or checked filter changes
  useEffect(() => {
    let filtered = stringTypeSettings;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((setting) =>
        setting.Name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (showCheckedOnly) {
      filtered = filtered.filter((setting) => setting.ss_status === 1);
    }

    setFilteredSettings(filtered);
  }, [searchTerm, showCheckedOnly, stringTypeSettings]);

  const cashaccref = useRef(null);
  const toolbarref = useRef(null);
  const stockvalref = useRef(null);
  const locref = useRef(null);
  const [cashAcc, setcashAcc] = useState([]);

  const selectcashAcc = async () => {
    try {
      if (!dbCode) return;
      const response = await axios.get(
        `${apiBaseUrl}/main/SchRec_caba/${dbCode}`
      );
      const caba = response.data;
      if (Array.isArray(caba)) {
        const transformedData = caba.map((item) => [item.ledcode, item.LedName]);
        setcashAcc(transformedData);
      }
    } catch (error) {
      console.error("Error fetching selectcashAcc Values:", error.message);
    }
  };

  useEffect(() => {
    selectcashAcc();
  }, [apiBaseUrl, userType, dbCode]);

  const getComboOptions = (currentValue, defaultOptions = []) => {
    if (
      currentValue &&
      !defaultOptions.some((option) => option[1] === currentValue)
    ) {
      return [[currentValue, currentValue], ...defaultOptions];
    }
    return defaultOptions;
  };

  const handleInputChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    updateSettingValue(field, value);
  };

  const updateSettingValue = (field, value) => {
    const settingMap = {
      decimalPoint: "DECIMAL POINT",
      inputColor: "INPUT COLOUR",
      cashAccount: "CASH A/C",
      toolbarSales: "TOOLBAR SALES",
      stockMethod: "STOCK METHOD",
      defaultLocation: "LOCATION",
      decimalPointInReports: "DECIMAL POINT IN REPORT",
    };

    const settingName = settingMap[field];
    if (settingName) {
      setUpdatedSettings((prev) => {
        const exists = prev.some((s) => s.Name === settingName);
        if (exists) {
          return prev.map((setting) =>
            setting.Name === settingName
              ? { ...setting, s_Value: value, DDate: new Date().toISOString() }
              : setting
          );
        } else {
          return [
            ...prev,
            {
              Auto: 999999,
              Name: settingName,
              ss_status: 1,
              Type: "C",
              s_Value: value,
              DDate: new Date().toISOString(),
            },
          ];
        }
      });
    }
  };

  const handleCheckboxChange = (index, checked, settingItem = null) => {
    setstringTypeSettings((prev) =>
      prev.map((s) =>
        (settingItem && s.Name === settingItem.Name) ||
        (index >= 0 && s === prev[index])
          ? { ...s, ss_status: checked ? 1 : 0 }
          : s
      )
    );

    setUpdatedSettings((prev) => {
      const nameToMatch = settingItem
        ? settingItem.Name
        : updatedSettings[index]?.Name;
      if (!nameToMatch) return prev;

      const exists = prev.some((s) => s.Name === nameToMatch);
      if (exists) {
        return prev.map((s) =>
          s.Name === nameToMatch
            ? {
                ...s,
                ss_status: checked ? 1 : 0,
                DDate: new Date().toISOString(),
              }
            : s
        );
      } else {
        return [
          ...prev,
          {
            Auto: 999999,
            Name: nameToMatch,
            ss_status: checked ? 1 : 0,
            Type: "S",
            s_Value: "",
            DDate: new Date().toISOString(),
          },
        ];
      }
    });
  };

  const handleSave = async () => {
    if (!window.confirm("Do you want to Save & Apply General Settings?")) {
      return;
    }
    try {
      setIsSaving(true);
      const response = await axios.post(
        `${apiBaseUrl}/common/updateSettings/${dbCode}`,
        {
          settingsData: updatedSettings,
        }
      );

      if (response.data && response.data.success) {
        // Fetch fresh settings and update Redux so the whole app updates immediately
        try {
          const freshRes = await axios.get(`${apiBaseUrl}/common/getSettings/${dbCode}`);
          if (freshRes.data && freshRes.data.success) {
            dispatch(setSoftwareSettings(freshRes.data.data));
          }
        } catch (fetchErr) {
          console.error("Error refreshing redux settings:", fetchErr);
        }
        alert("General settings saved successfully!");
      } else {
        alert("Failed to save settings: " + (response.data?.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Error saving settings: " + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const refreshSettings = async () => {
    try {
      if (!dbCode) return;
      const res = await axios.get(`${apiBaseUrl}/common/getSettings/${dbCode}`);
      if (res.data && res.data.success) {
        dispatch(setSoftwareSettings(res.data.data));
      }
    } catch (err) {
      console.error("Error refreshing general settings:", err);
    }
  };

  useImperativeHandle(ref, () => ({
    handleSave,
    refreshSettings,
  }));

  return (
    <div className="w-full space-y-3 font-['Inter',sans-serif] [&_*]:!font-['Inter',sans-serif]">
      {/* Hidden button for parent save trigger */}
      <button
        id="sw-settings-submit-btn"
        onClick={handleSave}
        className="hidden"
        type="button"
      />

      {/* 2-Column Responsive Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
        {/* LEFT COLUMN: Scheme & Policy Controls (7 cols) */}
        <div className="lg:col-span-7 bg-white rounded-lg border border-slate-200 p-3.5 shadow-sm space-y-2.5 font-['Inter',sans-serif]">
          {/* Section Header & Search / Filter Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-slate-100">
            <div className="flex items-center space-x-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-600" />
              <h2 className="font-bold text-slate-800 text-xs tracking-tight uppercase font-['Inter',sans-serif]">
                Scheme &amp; Policy Controls
              </h2>
              <span className="text-[10px] bg-slate-100 font-semibold px-1.5 py-0.2 rounded text-slate-600 font-['Inter',sans-serif]">
                {filteredSettings.length} Rules
              </span>
            </div>

            {/* Search Input & Checked Filter */}
            <div className="flex items-center space-x-2">
              <div className="relative flex-1 sm:w-48">
                <FiSearch className="absolute left-2.5 top-2 text-slate-400 text-[10px] pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search settings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-7 pr-2.5 py-1 bg-slate-50 border border-slate-200 rounded-md text-[11px] text-slate-700 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition outline-none font-['Inter',sans-serif]"
                />
              </div>
              <label className="flex items-center space-x-1 cursor-pointer text-[11px] font-medium text-slate-600 select-none font-['Inter',sans-serif]">
                <input
                  type="checkbox"
                  checked={showCheckedOnly}
                  onChange={(e) => setShowCheckedOnly(e.target.checked)}
                  className="w-3 h-3 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="whitespace-nowrap">Checked Only</span>
              </label>
            </div>
          </div>

          {/* Structured Checklist Cards */}
          <div className="space-y-1.5 max-h-[calc(90vh-250px)] 2xl:max-h-[500px] overflow-y-auto pr-1">
            {filteredSettings.length > 0 ? (
              filteredSettings.map((setting, idx) => {
                const isChecked = setting.ss_status === 1;
                const isWhatsApp = setting.Name.includes("WHATSAPP");
                const description =
                  SETTING_DESCRIPTIONS[setting.Name] ||
                  "Workstation policy control rule for transaction processing.";

                return (
                  <label
                    key={setting.Auto || setting.Name || idx}
                    className={`flex items-start p-2 rounded-md border transition cursor-pointer select-none font-['Inter',sans-serif] ${
                      isChecked
                        ? "border-blue-200 bg-blue-50/40 hover:bg-blue-50/70"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/60"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) =>
                        handleCheckboxChange(
                          stringTypeSettings.findIndex(
                            (s) => s.Name === setting.Name
                          ),
                          e.target.checked,
                          setting
                        )
                      }
                      className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 transition cursor-pointer shrink-0"
                    />
                    <div className="ml-2.5 flex-1">
                      <div className="flex items-center space-x-1.5">
                        <span className="text-[11px] font-bold text-slate-900 tracking-tight block font-['Inter',sans-serif]">
                          {setting.Name}
                        </span>
                        {isWhatsApp && (
                          <span className="text-[9px] bg-emerald-100 text-emerald-800 font-semibold px-1 py-0.2 rounded flex items-center font-['Inter',sans-serif]">
                            <FaWhatsapp className="mr-0.5 text-emerald-600 text-[10px]" /> Auto-API
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-slate-500 mt-0.5 block leading-tight font-['Inter',sans-serif]">
                        {description}
                      </span>
                    </div>
                  </label>
                );
              })
            ) : (
              <div className="p-6 text-center text-[11px] text-slate-400 bg-slate-50 rounded-md border border-dashed border-slate-200 font-['Inter',sans-serif]">
                No workstation settings matching standard query.
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Operational Parameters & Preferences (5 cols) */}
        <div className="lg:col-span-5 space-y-3 font-['Inter',sans-serif]">
          {/* Main Parameters Card */}
          <div className="bg-white rounded-lg border border-slate-200 p-3.5 shadow-sm space-y-2.5 font-['Inter',sans-serif]">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center space-x-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <h2 className="font-bold text-slate-800 text-xs tracking-tight uppercase font-['Inter',sans-serif]">
                  Operational Parameters
                </h2>
              </div>
              <span className="text-[10px] text-slate-400 font-medium font-['Inter',sans-serif]">Terminal Defaults</span>
            </div>

            {/* Form Controls Stack */}
            <div className="space-y-2.5 text-[11px] font-['Inter',sans-serif]">
              {/* Theme & Accent Colour */}
              <div>
                <label className="block font-semibold text-slate-700 mb-1 text-[11px] font-['Inter',sans-serif]">
                  Theme &amp; Accent Colour
                </label>
                <div className="flex items-center space-x-1.5">
                  <div
                    className="w-8 h-7 rounded border border-slate-300 shadow-inner flex items-center justify-center cursor-pointer overflow-hidden p-0 relative shrink-0"
                    style={{ backgroundColor: formValues.inputColor }}
                  >
                    <FiDroplet className="text-slate-800 text-[10px] drop-shadow-sm" />
                    <input
                      type="color"
                      value={formValues.inputColor}
                      onChange={(e) => handleInputChange("inputColor", e.target.value)}
                      className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                    />
                  </div>
                  <input
                    type="text"
                    value={formValues.inputColor}
                    onChange={(e) => handleInputChange("inputColor", e.target.value)}
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1 text-[11px] text-slate-700 font-medium focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition outline-none font-['Inter',sans-serif]"
                  />
                  {/* Swatches */}
                  <div className="flex items-center space-x-1 pl-0.5">
                    <button
                      type="button"
                      onClick={() => handleInputChange("inputColor", "#FFD700")}
                      className="w-4 h-4 rounded-full bg-amber-400 hover:scale-110 transition shadow-sm border border-amber-500 cursor-pointer"
                      title="Gold"
                    />
                    <button
                      type="button"
                      onClick={() => handleInputChange("inputColor", "#10B981")}
                      className="w-4 h-4 rounded-full bg-emerald-500 hover:scale-110 transition shadow-sm border border-emerald-600 cursor-pointer"
                      title="Emerald"
                    />
                    <button
                      type="button"
                      onClick={() => handleInputChange("inputColor", "#1E3A8A")}
                      className="w-4 h-4 rounded-full bg-blue-900 hover:scale-110 transition shadow-sm border border-blue-950 cursor-pointer"
                      title="Royal Navy"
                    />
                    <button
                      type="button"
                      onClick={() => handleInputChange("inputColor", "#E11D48")}
                      className="w-4 h-4 rounded-full bg-rose-600 hover:scale-110 transition shadow-sm border border-rose-700 cursor-pointer"
                      title="Ruby"
                    />
                  </div>
                </div>
              </div>

              {/* Company Cash A/C */}
              <div>
                <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                  Company Cash A/C
                </label>
                <div className="relative font-['Inter',sans-serif]">
                  <ComboBox
                    type="text"
                    options={getComboOptions(formValues.cashAccount, cashAcc.length > 0 ? cashAcc : [["CASH", "CASH (Main Showroom Cash Account)"]])}
                    comboRef={cashaccref}
                    findedValue={formValues.cashAccount}
                    onInputChange={(value) =>
                      handleInputChange(
                        "cashAccount",
                        Array.isArray(value) ? value[1] : value
                      )
                    }
                    className="w-full font-['Inter',sans-serif]"
                    inputClassName="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1 text-[11px] font-semibold text-slate-800 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition outline-none font-['Inter',sans-serif]"
                  />
                </div>
              </div>

              {/* Toolbar Sales ID & Default Location */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                    Toolbar Sales ID
                  </label>
                  <input
                    type="text"
                    value={formValues.toolbarSales}
                    onChange={(e) => handleInputChange("toolbarSales", e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1 text-[11px] font-bold text-slate-800 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition outline-none font-['Inter',sans-serif]"
                  />
                  <span className="text-[9px] text-slate-400 font-['Inter',sans-serif]">POS register shortcut</span>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                    Default Location
                  </label>
                  <input
                    type="text"
                    value={formValues.defaultLocation}
                    onChange={(e) => handleInputChange("defaultLocation", e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1 text-[11px] font-bold text-slate-800 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition outline-none font-['Inter',sans-serif]"
                  />
                  <span className="text-[9px] text-slate-400 font-['Inter',sans-serif]">Warehouse/Display floor</span>
                </div>
              </div>

              {/* Stock Valuation Method */}
              <div>
                <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                  Stock Valuation Method
                </label>
                <div className="relative font-['Inter',sans-serif]">
                  <select
                    value={formValues.stockMethod}
                    onChange={(e) => handleInputChange("stockMethod", e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1 text-[11px] font-semibold text-slate-800 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition outline-none appearance-none pr-7 cursor-pointer font-['Inter',sans-serif]"
                  >
                    <option value="REAL">REAL (Real-Time Weighted Average / FIFO)</option>
                    <option value="STANDARD COST">STANDARD COST (Fixed benchmark)</option>
                    <option value="DAILY MC BULLION COST">DAILY MC BULLION COST</option>
                  </select>
                  <FiChevronDown className="absolute right-2.5 top-2 text-slate-400 pointer-events-none text-[10px]" />
                </div>
              </div>

              {/* Decimals Configuration Card */}
              <div className="p-2.5 bg-slate-50 rounded-md border border-slate-200 space-y-1.5 font-['Inter',sans-serif]">
                <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block font-['Inter',sans-serif]">
                  Precision &amp; Decimals Formatting
                </span>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block font-medium text-slate-600 mb-0.5 text-[10px] font-['Inter',sans-serif]">
                      Decimal (Weight/Qty)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="4"
                      value={formValues.decimalPoint}
                      onChange={(e) => handleInputChange("decimalPoint", e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-md px-2 py-0.5 text-[11px] font-bold text-slate-800 text-center focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition outline-none font-['Inter',sans-serif]"
                    />
                    <span className="text-[9px] text-slate-400 mt-0.5 block font-['Inter',sans-serif]">
                      0.001g (Grams)
                    </span>
                  </div>
                  <div>
                    <label className="block font-medium text-slate-600 mb-0.5 text-[10px] font-['Inter',sans-serif]">
                      Decimal In Reports (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="3"
                      value={formValues.decimalPointInReports}
                      onChange={(e) =>
                        handleInputChange("decimalPointInReports", e.target.value)
                      }
                      className="w-full bg-white border border-slate-300 rounded-md px-2 py-0.5 text-[11px] font-bold text-slate-800 text-center focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition outline-none font-['Inter',sans-serif]"
                    />
                    <span className="text-[9px] text-slate-400 mt-0.5 block font-['Inter',sans-serif]">
                      ₹ 0.00 Currency
                    </span>
                  </div>
                </div>
              </div>

              {/* Direct Save Action Button inside Panel */}
              <div className="pt-1.5 font-['Inter',sans-serif]">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="w-full h-8 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold rounded-lg text-xs shadow-sm transition flex items-center justify-center space-x-1.5 cursor-pointer disabled:opacity-50 font-['Inter',sans-serif]"
                >
                  <FiSave className="w-3.5 h-3.5 shrink-0" />
                  <span>{isSaving ? "Saving Settings..." : "Save & Apply General Settings"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default GeneralSettings;
